import { useEffect, useRef, useState } from 'react'

import { HtmlLayout } from '@karakuri-web/faq-type'

interface Props {
  layout: HtmlLayout
}

/**
 * HTMLウィジェット
 * カスタムHTMLコンテンツを表示する
 */
export function HtmlWidget({ layout }: Props) {
  if (!layout.data) {
    return null
  }
  return (
    <div className="faq-html">
      {layout.sandboxed ? (
        <SandboxedIframe content={layout.data} />
      ) : (
        <Iframe content={layout.data} />
      )}
    </div>
  )
}

interface IframeProps {
  content?: string
}

function Iframe(props: IframeProps) {
  const writeHTML = (frame: HTMLIFrameElement | null) => {
    if (!props.content || !frame || !frame.contentDocument) {
      return
    }
    const doc = frame.contentDocument
    frame.style.height = '0px'
    doc.open()
    doc.write(props.content)
    doc.close()
    frame.style.width = '100%'
    frame.style.height = `${frame.contentWindow?.document.body.scrollHeight}px`
  }
  // eslint-disable-next-line jsx-a11y/iframe-has-title
  return <iframe src="about:blank" className="html-widget-iframe" ref={writeHTML} />
}

export const HTML_WIDGET_HEIGHT_MESSAGE = 'krkr-faq-html-widget-height'

// サンドボックスの中からは iframe の外が読めないため、中から高さを知らせる（Iframe と同じく body の scrollHeight）
const REPORT_HEIGHT_SCRIPT = `<script>(function () {
  function report() {
    parent.postMessage({ type: '${HTML_WIDGET_HEIGHT_MESSAGE}', height: document.body ? document.body.scrollHeight : 0 }, '*')
  }
  window.addEventListener('load', report)
  if (window.ResizeObserver) {
    new ResizeObserver(report).observe(document.documentElement)
  }
  report()
})()</script>`

/**
 * 管理画面と同じオリジンで動く画面（faq-preview）向け。allow-same-origin を付けないので中身は別のオリジン扱いになり、
 * スクリプトを実行しても管理画面のトークン・Cookie・DOM には届かない
 */
function SandboxedIframe({ content }: { content: string }) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const { data } = event
      if (
        event.source !== frameRef.current?.contentWindow ||
        typeof data !== 'object' ||
        data === null ||
        data.type !== HTML_WIDGET_HEIGHT_MESSAGE ||
        typeof data.height !== 'number' ||
        !Number.isFinite(data.height)
      ) {
        return
      }
      setHeight(Math.max(0, data.height))
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  return (
    <iframe
      ref={frameRef}
      title="HTMLウィジェット"
      className="html-widget-iframe"
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms"
      srcDoc={`${content}${REPORT_HEIGHT_SCRIPT}`}
      style={{ width: '100%', height: `${height}px` }}
    />
  )
}
